import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { CheckCircle2 } from "lucide-react";

export function StaffField({ field, value, onChange, readOnly = false }) {
  const id = `sf-${field.key}`;
  if (field.type === "textarea") {
    return (
      <div className="sm:col-span-2">
        <Label htmlFor={id}>{field.label}</Label>
        <Textarea id={id} rows={3} value={value || ""} onChange={(e) => onChange(field.key, e.target.value)} className="mt-1.5" disabled={readOnly} data-testid={id} />
      </div>
    );
  }
  if (field.type === "date") {
    return (
      <div>
        <Label htmlFor={id}>{field.label}</Label>
        <Input id={id} type="date" value={value || ""} onChange={(e) => onChange(field.key, e.target.value)} className="mt-1.5" disabled={readOnly} data-testid={id} />
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <div>
        <Label>{field.label}</Label>
        <Select value={value || ""} onValueChange={(v) => onChange(field.key, v)} disabled={readOnly}>
          <SelectTrigger className="mt-1.5" data-testid={id}><SelectValue placeholder="Choose..." /></SelectTrigger>
          <SelectContent>
            {field.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }
  if (field.type === "yesno") {
    return (
      <div className="flex items-center justify-between gap-4 rounded-xl border border-line bg-ivory px-4 py-3">
        <span className="text-sm text-ink">{field.label}</span>
        <div className="flex gap-2">
          {["Yes", "No"].map((v) => (
            <button
              key={v}
              type="button"
              disabled={readOnly}
              onClick={() => onChange(field.key, v)}
              data-testid={`${id}-${v.toLowerCase()}`}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all disabled:opacity-60 ${value === v ? (v === "Yes" ? "bg-champagne-dark text-white" : "bg-sage text-white") : "bg-surface border border-line text-mutedink"}`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (field.type === "checks") {
    const selected = Array.isArray(value) ? value : [];
    return (
      <div className="sm:col-span-2">
        <Label>{field.label}</Label>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {field.options.map((o) => {
            const checked = selected.includes(o);
            return (
              <button
                key={o}
                type="button"
                disabled={readOnly}
                onClick={() => onChange(field.key, checked ? selected.filter((x) => x !== o) : [...selected, o])}
                data-testid={`${id}-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className={`flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left text-xs transition-all disabled:opacity-60 ${checked ? "border-teal bg-teal-light text-teal font-medium" : "border-line bg-ivory text-ink hover:border-teal"}`}
              >
                <span className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${checked ? "bg-teal border-teal" : "border-line bg-surface"}`}>
                  {checked && <CheckCircle2 className="h-3 w-3 text-white" />}
                </span>
                {o}
              </button>
            );
          })}
        </div>
      </div>
    );
  }
  return (
    <div>
      <Label htmlFor={id}>{field.label}</Label>
      <Input id={id} value={value || ""} onChange={(e) => onChange(field.key, e.target.value)} className="mt-1.5" disabled={readOnly} data-testid={id} />
    </div>
  );
}

export default function FormRenderer({ sections, answers, onChange, readOnly = false }) {
  return (
    <div className="space-y-8">
      {sections.map((section, si) => (
        <div key={section.title || si}>
          {section.title && <h3 className="text-sm font-sans font-semibold text-teal mb-3">{section.title}</h3>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {section.fields.map((f) => (
              <StaffField key={f.key} field={f} value={answers[f.key]} onChange={onChange} readOnly={readOnly} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
